/**
 * Sharing a ruling.
 *
 * The link points at the webapp; the backend resolves the id to the stored
 * conversation once `is_public` is set (see `api/conversations`).
 */
export function answerShareUrl(conversationId: string): string {
  return new URL(
    `/s/${encodeURIComponent(conversationId)}`,
    window.location.origin,
  ).toString();
}

/**
 * Server-built links, keyed by conversation id. The server builds the URL from
 * `APP_URL`, which differs from `window.location.origin` when developing
 * locally; we cache it after a share so the next one can skip the round-trip.
 */
const canonicalLinks = new Map<string, string>();

/**
 * The link to hand out for a conversation: the canonical one when a previous
 * share learned it, otherwise the current origin. Never waits on the server —
 * see `AnswerActions.doShare` for why that matters.
 */
export function shareLink(conversationId: string): string {
  return canonicalLinks.get(conversationId) ?? answerShareUrl(conversationId);
}

/** Record the server's canonical link for a conversation. */
export function rememberShareLink(
  conversationId: string,
  url?: string | null,
): void {
  if (url) canonicalLinks.set(conversationId, url);
}

export type ShareResult = "shared" | "copied" | "dismissed" | "unavailable";

/** Use the native share sheet where available, otherwise copy the link. */
export async function shareUrl(url: string): Promise<ShareResult> {
  if (typeof navigator.share === "function") {
    try {
      await navigator.share({ title: "FAB Judge ruling", url });
      return "shared";
    } catch (error) {
      // The user dismissed the sheet: nothing more to do. Any other failure
      // (most commonly `NotAllowedError` when the call lost the user gesture)
      // falls through to the clipboard so the link still reaches the user.
      if (isDismissal(error)) return "dismissed";
    }
  }

  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(url);
      return "copied";
    } catch {
      // Fall through to the legacy path (e.g. non-secure LAN origin).
    }
  }

  return legacyCopy(url) ? "copied" : "unavailable";
}

/** `AbortError` is how every engine reports a share sheet the user closed. */
function isDismissal(error: unknown): boolean {
  // `DOMException` is `Error`-derived in current engines but not in all of
  // them, so read the name rather than relying on `instanceof`.
  return (error as { name?: unknown } | null | undefined)?.name ===
    "AbortError";
}

/** Deprecated but still the only option on insecure origins. */
function legacyCopy(text: string): boolean {
  try {
    const el = document.createElement("textarea");
    el.value = text;
    el.setAttribute("readonly", "");
    el.style.position = "fixed";
    el.style.top = "-1000px";
    el.style.opacity = "0";
    document.body.appendChild(el);
    el.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(el);
    return ok;
  } catch {
    return false;
  }
}
